import React, { useState, useEffect } from 'react';
import {
  X, Share2, Link, Copy, Check, Lock, Smartphone, Eye, EyeOff,
  Calendar, Download, QrCode, Loader2, Trash2, RefreshCw,
  Shield, Clock, Users, Globe, AlertTriangle, ChevronDown, ChevronUp,
  Sparkles, KeyRound, Monitor, MapPin, FileText, Activity, History
} from 'lucide-react';
import { toast, translateBackendError } from '../../../utils/toastUtils';
import api from '../../../services/api/client';
import { useI18n } from '../../../i18n';

const WorkspaceShareDialog = ({ isOpen, onClose, workspace }) => {
  const { t } = useI18n();
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedShare, setExpandedShare] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [accessLogs, setAccessLogs] = useState({});
  const [loadingLogs, setLoadingLogs] = useState({});

  // Parse user agent to get browser/device info
  const parseUserAgent = (ua) => {
    if (!ua) return { browser: 'Unknown', device: 'Unknown', os: 'Unknown' };

    let browser = 'Unknown';
    let device = 'Desktop';
    let os = 'Unknown';

    // Detect browser
    if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
    else if (ua.includes('Edg')) browser = 'Edge';
    else if (ua.includes('Opera') || ua.includes('OPR')) browser = 'Opera';
    else if (ua.includes('MSIE') || ua.includes('Trident')) browser = 'Internet Explorer';

    // Detect device
    if (ua.includes('Mobile') || ua.includes('Android')) device = 'Mobile';
    else if (ua.includes('Tablet') || ua.includes('iPad')) device = 'Tablet';

    // Detect OS
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac OS')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iOS') || ua.includes('iPhone')) os = 'iOS';

    return { browser, device, os };
  };

  // Get access type label and color
  const getAccessTypeInfo = (type) => {
    const types = {
      'view': { label: 'View', color: 'text-blue-400', bg: 'bg-blue-500/20' },
      'download': { label: 'Download', color: 'text-green-400', bg: 'bg-green-500/20' },
      'password_attempt': { label: 'Password attempt', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
      'sms_request': { label: 'SMS request', color: 'text-purple-400', bg: 'bg-purple-500/20' },
      'sms_verify': { label: 'SMS verify', color: 'text-purple-400', bg: 'bg-purple-500/20' },
      'expired_access': { label: 'Expired access', color: 'text-red-400', bg: 'bg-red-500/20' },
      'limit_reached': { label: 'Limit reached', color: 'text-red-400', bg: 'bg-red-500/20' }
    };
    return types[type] || { label: type, color: 'text-gray-400', bg: 'bg-gray-500/20' };
  };

  // Fetch access logs for a share
  const fetchAccessLogs = async (shareId) => {
    try {
      setLoadingLogs(prev => ({ ...prev, [shareId]: true }));
      const response = await api.get(`/workspace-share/${shareId}`);
      if (response.data.access_logs) {
        setAccessLogs(prev => ({ ...prev, [shareId]: response.data.access_logs }));
      }
    } catch (error) {
      console.error('Error fetching access logs:', error);
    } finally {
      setLoadingLogs(prev => ({ ...prev, [shareId]: false }));
    }
  };

  // Handle expand/collapse with log fetching
  const handleExpandShare = (shareId) => {
    if (expandedShare === shareId) {
      setExpandedShare(null);
    } else {
      setExpandedShare(shareId);
      if (!accessLogs[shareId]) {
        fetchAccessLogs(shareId);
      }
    }
  };

  // Generate default name based on workspace
  const getDefaultName = () => {
    return `Pentest results for ${workspace?.name || 'Client'}`;
  };

  // Generate complex password (16-20 characters)
  const generateComplexPassword = () => {
    const length = Math.floor(Math.random() * 5) + 16; // 16-20 characters
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    const allChars = uppercase + lowercase + numbers + symbols;

    // Ensure at least one of each type
    let password = '';
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];

    // Fill remaining characters
    for (let i = password.length; i < length; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }

    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  const copyPassword = () => {
    if (formData.password) {
      navigator.clipboard.writeText(formData.password);
      setCopiedPassword(true);
      toast.success(t('workspaces.share.passwordCopied', 'Password copied'));
      setTimeout(() => setCopiedPassword(false), 2000);
    }
  };

  // Form state
  const [formData, setFormData] = useState({
    auth_method: 'password', // Password by default
    password: '',
    phone_number: '',
    max_views: '',
    expires_in_hours: '168', // 7 days default
    download_allowed: false,
    name: '',
    description: ''
  });

  useEffect(() => {
    if (isOpen && workspace?.id) {
      fetchShares();
    }
  }, [isOpen, workspace?.id]);

  const fetchShares = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/workspace-share/list?workspace_id=${workspace.id}`);
      setShares(response.data.shares || []);
    } catch (error) {
      console.error('Error fetching shares:', error);
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateShare = async () => {
    try {
      setCreating(true);

      const payload = {
        workspace_id: workspace.id,
        auth_method: formData.auth_method,
        download_allowed: formData.download_allowed,
        name: formData.name || getDefaultName(),
        description: formData.description || null
      };

      // Add optional fields
      if (formData.auth_method === 'password' || formData.auth_method === 'both') {
        if (!formData.password || formData.password.length < 12) {
          toast.error(t('workspaces.share.passwordMinLength', 'Password must be at least 12 characters'));
          return;
        }
        payload.password = formData.password;
      }

      if (formData.auth_method === 'sms' || formData.auth_method === 'both') {
        if (!formData.phone_number) {
          toast.error(t('workspaces.share.phoneRequired', 'Phone number is required'));
          return;
        }
        payload.phone_number = formData.phone_number;
      }

      if (formData.max_views) {
        payload.max_views = parseInt(formData.max_views);
      }

      if (formData.expires_in_hours) {
        payload.expires_in_hours = parseInt(formData.expires_in_hours);
      }

      const response = await api.post('/workspace-share/create', payload);

      toast.success(t('workspaces.share.linkCreated', 'Share link created successfully'));
      setShares([response.data.share, ...shares]);
      setShowCreateForm(false);
      resetForm();

      // Copy link to clipboard
      if (response.data.share_url) {
        navigator.clipboard.writeText(response.data.share_url);
        toast.success(t('workspaces.share.linkCopied', 'Link copied'));
      }
    } catch (error) {
      console.error('Error creating share:', error);
      toast.error(translateBackendError(error.response?.data?.detail) || t('errors.generic'));
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeShare = async (shareId) => {
    try {
      await api.post(`/workspace-share/${shareId}/revoke`);
      toast.success(t('workspaces.share.revoked', 'Share revoked'));
      fetchShares();
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleDeleteShare = async (shareId) => {
    if (!confirm(t('common.confirmDeleteItem'))) return;

    try {
      await api.delete(`/workspace-share/${shareId}`);
      toast.success(t('workspaces.share.deleted', 'Share deleted'));
      setShares(shares.filter(s => s.id !== shareId));
    } catch (error) {
      toast.error(t('errors.deleteError', 'Error deleting'));
    }
  };

  const copyShareLink = (share) => {
    const baseUrl = window.location.origin;
    const shareUrl = `${baseUrl}/share/${share.share_token}`;
    navigator.clipboard.writeText(shareUrl);
    setCopiedId(share.id);
    toast.success(t('workspaces.share.linkCopied', 'Link copied'));
    setTimeout(() => setCopiedId(null), 2000);
  };

  const resetForm = () => {
    setFormData({
      auth_method: 'password', // Password by default
      password: '',
      phone_number: '',
      max_views: '',
      expires_in_hours: '168',
      download_allowed: false,
      name: '',
      description: ''
    });
    setShowPassword(false);
    setCopiedPassword(false);
  };

  const getAuthMethodLabel = (method) => {
    const labels = {
      'none': 'None',
      'password': 'Password',
      'sms': 'SMS code',
      'both': 'Password + SMS'
    };
    return labels[method] || method;
  };

  const getAuthMethodIcon = (method) => {
    switch (method) {
      case 'password':
        return <Lock className="w-4 h-4 text-yellow-400" />;
      case 'sms':
        return <Smartphone className="w-4 h-4 text-blue-400" />;
      case 'both':
        return <Shield className="w-4 h-4 text-purple-400" />;
      default:
        return <Globe className="w-4 h-4 text-gray-400" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-brand-black-light rounded-xl border border-brand-green/30 w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-brand-green/20 rounded-lg">
              <Share2 className="w-6 h-6 text-brand-green" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Share workspace</h2>
              <p className="text-sm text-gray-400">{workspace?.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Create New Share Button */}
          {!showCreateForm && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="w-full p-4 border-2 border-dashed border-brand-green/30 rounded-xl hover:border-brand-green/50 hover:bg-brand-green/5 transition-colors flex items-center justify-center gap-3 text-brand-green"
            >
              <Link className="w-5 h-5" />
              <span>Create a new share link</span>
            </button>
          )}

          {/* Create Form */}
          {showCreateForm && (
            <div className="bg-brand-black rounded-xl border border-gray-700 p-5 space-y-5">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">New share</h3>
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Name */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Share name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={getDefaultName()}
                    className="w-full px-3 py-2 bg-brand-black border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green"
                  />
                </div>

                {/* Auth Method */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Protection
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { value: 'none', label: 'None', icon: Globe, color: 'gray' },
                      { value: 'password', label: 'Password', icon: Lock, color: 'yellow' },
                      { value: 'sms', label: 'SMS code', icon: Smartphone, color: 'blue' },
                      { value: 'both', label: 'Both', icon: Shield, color: 'purple' }
                    ].map(option => (
                      <button
                        key={option.value}
                        onClick={() => setFormData({ ...formData, auth_method: option.value })}
                        className={`p-3 rounded-lg border transition-colors flex flex-col items-center gap-2 ${
                          formData.auth_method === option.value
                            ? `bg-${option.color}-500/20 border-${option.color}-500/50 text-${option.color}-400`
                            : 'border-gray-600 text-gray-400 hover:border-gray-500'
                        }`}
                      >
                        <option.icon className="w-5 h-5" />
                        <span className="text-xs">{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Password */}
                {(formData.auth_method === 'password' || formData.auth_method === 'both') && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Password&nbsp;<span className="text-red-400">*</span>
                      <span className="text-xs text-gray-500 ml-2">(minimum 12 characters)</span>
                    </label>
                    <div className="relative flex gap-2">
                      <div className="relative flex-1">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          placeholder="Minimum 12 characters"
                          className={`w-full px-3 py-2 pr-10 bg-brand-black border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green ${
                            formData.password && formData.password.length < 12
                              ? 'border-red-500'
                              : formData.password.length >= 12
                                ? 'border-green-500'
                                : 'border-gray-600'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {/* Generate Password Button */}
                      <button
                        type="button"
                        onClick={() => {
                          const newPassword = generateComplexPassword();
                          setFormData({ ...formData, password: newPassword });
                          setShowPassword(true);
                          toast.success('Password generated (16-20 characters)');
                        }}
                        className="px-3 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors flex items-center gap-1"
                        title="Generate a complex password (16-20 characters)"
                      >
                        <Sparkles className="w-4 h-4" />
                      </button>
                      {/* Copy Password Button */}
                      <button
                        type="button"
                        onClick={copyPassword}
                        disabled={!formData.password}
                        className={`px-3 py-2 rounded-lg transition-colors flex items-center gap-1 ${
                          formData.password
                            ? 'bg-brand-green/20 text-brand-green hover:bg-brand-green/30'
                            : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        }`}
                        title="Copy password"
                      >
                        {copiedPassword ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                    {/* Password strength indicator */}
                    {formData.password && (
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 h-1 bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all ${
                              formData.password.length < 12
                                ? 'bg-red-500 w-1/4'
                                : formData.password.length < 16
                                  ? 'bg-yellow-500 w-2/4'
                                  : formData.password.length < 20
                                    ? 'bg-green-500 w-3/4'
                                    : 'bg-brand-green w-full'
                            }`}
                          />
                        </div>
                        <span className={`text-xs ${
                          formData.password.length < 12
                            ? 'text-red-400'
                            : formData.password.length < 16
                              ? 'text-yellow-400'
                              : 'text-green-400'
                        }`}>
                          {formData.password.length < 12
                            ? 'Too short'
                            : formData.password.length < 16
                              ? 'Fair'
                              : 'Strong'}
                          {' '}({formData.password.length} chars)
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Phone Number */}
                {(formData.auth_method === 'sms' || formData.auth_method === 'both') && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Phone number&nbsp;<span className="text-red-400">*</span>
                    </label>
                    <input
                      type="tel"
                      value={formData.phone_number}
                      onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                      placeholder="+33 6 12 34 56 78"
                      className="w-full px-3 py-2 bg-brand-black border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green"
                    />
                  </div>
                )}

                {/* Expiration */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Expiration
                  </label>
                  <select
                    value={formData.expires_in_hours}
                    onChange={(e) => setFormData({ ...formData, expires_in_hours: e.target.value })}
                    className="w-full px-3 py-2 bg-brand-black border border-gray-600 rounded-lg text-white focus:outline-none focus:border-brand-green"
                  >
                    <option value="">Never</option>
                    <option value="1">1 hour</option>
                    <option value="24">24 hours</option>
                    <option value="168">7 days</option>
                    <option value="720">30 days</option>
                    <option value="2160">90 days</option>
                  </select>
                </div>

                {/* Max Views */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Max views
                  </label>
                  <input
                    type="number"
                    value={formData.max_views}
                    onChange={(e) => setFormData({ ...formData, max_views: e.target.value })}
                    placeholder="Unlimited"
                    min="1"
                    className="w-full px-3 py-2 bg-brand-black border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green"
                  />
                </div>

                {/* Download Allowed */}
                <div className="col-span-2">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.download_allowed}
                      onChange={(e) => setFormData({ ...formData, download_allowed: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-600 bg-brand-black text-brand-green focus:ring-brand-green"
                    />
                    <span className="text-sm text-gray-300">Allow report download</span>
                  </label>
                </div>
              </div>

              {/* Create Button */}
              <div className="flex justify-end gap-3 pt-3 border-t border-gray-700">
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateShare}
                  disabled={creating}
                  className="btn-green px-6 py-2 rounded-lg disabled:opacity-50 flex items-center gap-2"
                >
                  {creating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Link className="w-4 h-4" />
                  )}
                  Create link
                </button>
              </div>
            </div>
          )}

          {/* Existing Shares */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Active share links</h3>
              <button
                onClick={fetchShares}
                disabled={loading}
                className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
              </div>
            ) : shares.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Share2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No active share links</p>
                <p className="text-sm">Create a link to share this workspace</p>
              </div>
            ) : (
              <div className="space-y-3">
                {shares.map(share => (
                  <div
                    key={share.id}
                    className={`bg-brand-black rounded-xl border transition-colors ${
                      share.is_active ? 'border-gray-700' : 'border-red-500/30 opacity-60'
                    }`}
                  >
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {getAuthMethodIcon(share.auth_method)}
                          <div>
                            <p className="text-white font-medium">{share.name || 'Unnamed share'}</p>
                            <div className="flex items-center gap-3 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <Eye className="w-3 h-3" />
                                {share.views_count || 0} views
                                {share.max_views && ` / ${share.max_views}`}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {share.expires_at
                                  ? new Date(share.expires_at).toLocaleDateString(undefined)
                                  : 'No expiration'}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => copyShareLink(share)}
                            className="p-2 text-gray-400 hover:text-brand-green rounded-lg hover:bg-gray-700 transition-colors"
                            title="Copy link"
                          >
                            {copiedId === share.id ? (
                              <Check className="w-4 h-4 text-green-400" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleExpandShare(share.id)}
                            className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700 transition-colors"
                            title="View details and history"
                          >
                            {expandedShare === share.id ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                          {share.is_active ? (
                            <button
                              onClick={() => handleRevokeShare(share.id)}
                              className="p-2 text-yellow-400 hover:text-yellow-300 rounded-lg hover:bg-yellow-500/10 transition-colors"
                              title="Revoke"
                            >
                              <AlertTriangle className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleDeleteShare(share.id)}
                              className="p-2 text-red-400 hover:text-red-300 rounded-lg hover:bg-red-500/10 transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {expandedShare === share.id && (
                        <div className="mt-4 pt-4 border-t border-gray-700 space-y-4">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Protection:</span>
                              <span className="ml-2 text-white">{getAuthMethodLabel(share.auth_method)}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Download:</span>
                              <span className="ml-2 text-white">{share.download_allowed ? 'Allowed' : 'Not allowed'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Created:</span>
                              <span className="ml-2 text-white">
                                {new Date(share.created_at).toLocaleDateString(undefined)}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">Status:</span>
                              <span className={`ml-2 ${share.is_active ? 'text-green-400' : 'text-red-400'}`}>
                                {share.is_active ? 'Active' : 'Revoked'}
                              </span>
                            </div>
                          </div>

                          {/* Share URL */}
                          <div className="p-3 bg-gray-800/50 rounded-lg">
                            <p className="text-xs text-gray-500 mb-1">Share link</p>
                            <code className="text-sm text-brand-green break-all">
                              {window.location.origin}/share/{share.share_token}
                            </code>
                          </div>

                          {/* Access History Section */}
                          <div className="mt-4">
                            <div className="flex items-center gap-2 mb-3">
                              <History className="w-4 h-4 text-brand-green" />
                              <h4 className="text-sm font-semibold text-white">Access history</h4>
                              <button
                                onClick={() => fetchAccessLogs(share.id)}
                                className="ml-auto p-1 text-gray-400 hover:text-white rounded transition-colors"
                                title="Refresh"
                              >
                                <RefreshCw className={`w-3 h-3 ${loadingLogs[share.id] ? 'animate-spin' : ''}`} />
                              </button>
                            </div>

                            {loadingLogs[share.id] ? (
                              <div className="flex items-center justify-center py-6">
                                <Loader2 className="w-5 h-5 text-brand-green animate-spin" />
                              </div>
                            ) : accessLogs[share.id]?.length > 0 ? (
                              <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                                {accessLogs[share.id].map((log, idx) => {
                                  const uaInfo = parseUserAgent(log.user_agent);
                                  const typeInfo = getAccessTypeInfo(log.access_type);
                                  return (
                                    <div
                                      key={log.id || idx}
                                      className={`p-3 rounded-lg border ${
                                        log.success ? 'bg-gray-800/50 border-gray-700' : 'bg-red-500/10 border-red-500/30'
                                      }`}
                                    >
                                      <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${typeInfo.bg} ${typeInfo.color}`}>
                                            {typeInfo.label}
                                          </span>
                                          {!log.success && (
                                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
                                              Failed
                                            </span>
                                          )}
                                        </div>
                                        <span className="text-xs text-gray-500">
                                          {new Date(log.accessed_at).toLocaleString(undefined, {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          })}
                                        </span>
                                      </div>
                                      <div className="grid grid-cols-2 gap-2 text-xs">
                                        <div className="flex items-center gap-1.5 text-gray-400">
                                          <Globe className="w-3 h-3" />
                                          <span className="text-white font-mono">{log.ip_address || 'N/A'}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 text-gray-400">
                                          <MapPin className="w-3 h-3" />
                                          <span className="text-white">
                                            {log.city && log.country ? `${log.city}, ${log.country}` : log.country || 'Unknown'}
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-1.5 text-gray-400">
                                          <Monitor className="w-3 h-3" />
                                          <span className="text-white">{uaInfo.browser} / {uaInfo.os}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 text-gray-400">
                                          <Smartphone className="w-3 h-3" />
                                          <span className="text-white">{uaInfo.device}</span>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="text-center py-6 text-gray-500">
                                <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">No access recorded</p>
                              </div>
                            )}

                            {/* Stats Summary */}
                            {accessLogs[share.id]?.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-gray-700 grid grid-cols-4 gap-2">
                                <div className="text-center p-2 bg-blue-500/10 rounded-lg">
                                  <div className="text-lg font-bold text-blue-400">
                                    {accessLogs[share.id].filter(l => l.access_type === 'view').length}
                                  </div>
                                  <div className="text-xs text-gray-400">Views</div>
                                </div>
                                <div className="text-center p-2 bg-green-500/10 rounded-lg">
                                  <div className="text-lg font-bold text-green-400">
                                    {accessLogs[share.id].filter(l => l.access_type === 'download').length}
                                  </div>
                                  <div className="text-xs text-gray-400">Downloads</div>
                                </div>
                                <div className="text-center p-2 bg-purple-500/10 rounded-lg">
                                  <div className="text-lg font-bold text-purple-400">
                                    {new Set(accessLogs[share.id].map(l => l.ip_address).filter(Boolean)).size}
                                  </div>
                                  <div className="text-xs text-gray-400">Unique IPs</div>
                                </div>
                                <div className="text-center p-2 bg-red-500/10 rounded-lg">
                                  <div className="text-lg font-bold text-red-400">
                                    {accessLogs[share.id].filter(l => !l.success).length}
                                  </div>
                                  <div className="text-xs text-gray-400">Failures</div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 bg-brand-black">
          <p className="text-xs text-gray-500 text-center">
            Share links allow your clients to securely access pentest results.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceShareDialog;
